import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@evi/skins';
import { fetchGameLeaderboard } from '@/lib/api';
import { allThemeGames, type ThemeGame } from '@/lib/themeGames';
import { ChargingGameOverlay, type GameSessionPayload } from '@/components/charging/ChargingGameOverlay';
import { useAuthStore } from '@/store/auth';
import { MapPage } from '@/pages/map/MapPage';
import { CabinetMotif } from './CabinetMotif';
import styles from './ArcadePage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * The hidden arcade — a bento grid over the dark room, one vertical
 * scroll (the horizontal snap-scroll is gone): hero panel (logo +
 * ARCADE, the driver's BEST game as the headline in that game's
 * accent, total points + rank as the supporting figures), a
 * leaderboard podium tile, then one tile per game in an alternating
 * size rhythm derived from ARRAY POSITION (never from namespaces),
 * so an eighth game slots in without a layout change.
 *
 * The room stays a position:fixed viewport backdrop (pure black to a
 * warm floor glow) — which is why /arcade keeps the crossfade enter
 * branch in AppShell: a transformed wrapper would become the fixed
 * element's containing block mid-animation.
 *
 * Everything non-visual is unchanged: gate on arcade_unlocked_at
 * (refresh once, then the map in place), THEME_GAMES as the source
 * with every game visible regardless of unlocked themes, tap mounts
 * the same ChargingGameOverlay with alert={null} and the arcade
 * payload, close returns here. Rank stays suppressed until at least
 * two players on the board have scored.
 */

// Static so the overlay's session effect never sees a new identity.
const ZERO_SESSION: GameSessionPayload = { kwh: 0, cost: '$0.00', elapsed: '0:00' };

/** Tile rhythm by grid position (cycles for an 8th+ game):
 *  tall = full-width ~200px, half = one column ~160px,
 *  short = full-width ~120px. */
const SIZE_PATTERN = ['tall', 'half', 'half', 'short', 'half', 'half', 'tall'] as const;
type TileSize = (typeof SIZE_PATTERN)[number];

export function ArcadePage({ onNavigate }: Props) {
  const { t } = useTranslation('arcade');
  // Light mode (follows the app's theme mode): the ROOM, hero and
  // podium go light — the game tiles stay dark cabinets. The motifs
  // are accent neon drawn for dark ground and would wash out on
  // white, and an arcade in daylight is exactly this: dark machines
  // standing in a lit hall.
  const { mode } = useTheme();
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const unlocked = Boolean(user?.arcade_unlocked_at);
  const [active, setActive] = useState<ThemeGame | null>(null);
  const [checked, setChecked] = useState(unlocked);

  // Freshly-unlocked drivers arrive with a stale store (the flag was
  // stamped server-side mid-conversation). Refresh once before giving
  // up; still locked after that → render the map IN PLACE, exactly
  // like an unknown path. Never navigate: a pushState bounce would
  // trap the Back button on /arcade and double-fire this gate through
  // the router's exit-overlay snapshot.
  useEffect(() => {
    if (unlocked || checked) return;
    void Promise.resolve(refreshUser()).finally(() => setChecked(true));
  }, [unlocked, checked, refreshUser]);

  const boardQ = useQuery({
    queryKey: ['leaderboard', 'games'],
    queryFn: fetchGameLeaderboard,
    staleTime: 60_000,
    enabled: unlocked,
  });
  const self = boardQ.data?.entries.find((e) => e.isSelf);
  // A rank is only honest with competition: until at least two
  // players on the board have scored, suppress it entirely — the
  // hero omits it AND the in-game arcade payload carries rank null.
  const scoredPlayers = boardQ.data?.entries.filter((e) => e.totalPoints > 0).length ?? 0;
  const rank = scoredPlayers >= 2 ? (boardQ.data?.selfRank ?? null) : null;
  const points = self?.totalPoints ?? 0;
  const games = allThemeGames();

  // Hero headline: the driver's BEST game — most points, not most
  // recent — rendered in that game's accent so the hero changes
  // character as their strongest game changes. Before any game has
  // scored, total points + a play prompt instead of a zeroed name.
  const best = useMemo(() => {
    const gp = self?.gamePoints ?? {};
    let top: { game: ThemeGame; points: number } | null = null;
    for (const g of games) {
      const p = gp[g.ns] ?? 0;
      if (p > 0 && (!top || p > top.points)) top = { game: g, points: p };
    }
    return top;
  }, [self?.gamePoints, games]);

  // Podium: top three scorers on the friends board (entries arrive
  // rank-sorted from the server); short rows fill with an invite
  // prompt rather than empty slots.
  const podium = (boardQ.data?.entries ?? []).filter((e) => e.totalPoints > 0).slice(0, 3);

  // Stable identity so the overlay's arcade re-post effect fires only
  // when the numbers actually change.
  const arcadePayload = useMemo(() => ({ rank, points }), [rank, points]);

  if (!checked && !unlocked) return null; // refresh in flight
  if (!unlocked) return <MapPage onNavigate={onNavigate} />;

  return (
    <div className={`${styles.page} ${mode === 'light' ? styles.light : ''}`}>
      <div className={styles.room} aria-hidden />

      {/* ── Hero: identity left, the driver's numbers right ──────── */}
      <button
        type="button"
        className={styles.hero}
        onClick={() => onNavigate('/leaderboard/friends')}
      >
        <span className={styles.heroLeft}>
          <img src="/evi-logo.png" alt="EV Initiative" className={styles.logo} />
          <span className={styles.heroWord}>ARCADE</span>
        </span>
        <span className={styles.heroRight}>
          {best ? (
            <>
              <span
                className={styles.heroBestName}
                style={{ '--accent': best.game.accent } as CSSProperties}
              >
                {best.game.name}
              </span>
              <span
                className={styles.heroBestPts}
                style={{ '--accent': best.game.accent } as CSSProperties}
              >
                {best.points.toLocaleString()} PTS
              </span>
              <span className={styles.heroTotal}>
                {t('totalLine', { pts: points.toLocaleString() })}
                {rank !== null ? ` · ${t('rank', { rank })}` : ''}
              </span>
            </>
          ) : (
            <>
              <span className={styles.heroPoints}>{points.toLocaleString()} PTS</span>
              <span className={styles.heroPlay}>{t('playFirst')}</span>
            </>
          )}
        </span>
      </button>

      {/* ── Bento grid ───────────────────────────────────────────── */}
      <div className={styles.grid}>
        <button
          type="button"
          className={`${styles.boardTile} ${styles.span2}`}
          onClick={() => onNavigate('/leaderboard/friends')}
        >
          <span className={styles.boardTitle}>{t('leaderboard')}</span>
          <span className={styles.podium}>
            {podium.map((e) => (
              <span key={e.userId} className={styles.podiumEntry}>
                <span className={styles.podiumAvatar}>
                  {(e.displayName || '?').slice(0, 1).toUpperCase()}
                </span>
                <span className={styles.podiumName}>{e.displayName}</span>
                <span className={styles.podiumPts}>{e.totalPoints.toLocaleString()}</span>
              </span>
            ))}
            {podium.length < 3 && (
              <span className={`${styles.podiumEntry} ${styles.podiumInvite}`}>
                <span className={styles.podiumAvatar}>+</span>
                <span className={styles.podiumName}>{t('invite')}</span>
              </span>
            )}
          </span>
        </button>

        {games.map((g, i) => {
          const size: TileSize = SIZE_PATTERN[i % SIZE_PATTERN.length] ?? 'half';
          return (
            <button
              key={g.ns}
              type="button"
              className={`${styles.tile} ${styles[`tile_${size}`]} ${size !== 'half' ? styles.span2 : ''}`}
              style={{ '--accent': g.accent } as CSSProperties}
              onClick={() => setActive(g)}
            >
              <CabinetMotif ns={g.ns} compact={size === 'half'} />
              <span className={styles.tileTheme}>{t(g.themeLabelKey)}</span>
              <span className={styles.tileName}>{g.name}</span>
              {size !== 'half' && (
                <span className={styles.tileDesc}>{t(g.descriptionKey)}</span>
              )}
            </button>
          );
        })}
      </div>

      {active && (
        <ChargingGameOverlay
          game={active}
          session={ZERO_SESSION}
          alert={null}
          alertText={() => ''}
          onClose={() => setActive(null)}
          title={active.name}
          closeLabel={t('close')}
          arcade={arcadePayload}
        />
      )}
    </div>
  );
}
